/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, UrlTree } from '@angular/router';
import { UserAccessService } from '@ao/shared/services/user-access.service';
import { LoggerService } from '@ao/core/services/logger.service';
import { Observable } from 'rxjs';
import { AllowedFeature } from '@ao/shared/interfaces/AllowedFeature';
import { map } from 'rxjs/operators';
import { RoleService } from '@ao/app/shared/services/role.service';
import { MenuReloadService } from '@ao/app/shared/services/menu-reload.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  private userAccess = inject(UserAccessService);
  private loggerService = inject(LoggerService);
  private roleService = inject(RoleService);
  private menuReloadService = inject(MenuReloadService);
  canActivate(
    route: ActivatedRouteSnapshot
  ):
    | boolean
    | UrlTree
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree> {
    const user = this.userAccess.getUserRoleFeatures();
    if (user) {
      this.roleService.setRoleByName(user.userRole?.roleName ?? '');
      this.menuReloadService.triggerReload();
      return this.checkRouteAccess(route, user);
    }
    return this.userAccess.getUserRoleFeaturesFromApi().pipe(
      map(apiUser => {
        if (!apiUser) {
          this.loggerService.logError(
            'Please select client to access this feature.'
          );
          return false;
        }
        this.roleService.setRoleByName(apiUser.userRole?.roleName ?? '');
        this.menuReloadService.triggerReload();
        return this.checkRouteAccess(route, apiUser);
      })
    );
  }

  private checkRouteAccess(route: ActivatedRouteSnapshot, user: any): boolean {
    if (route.data['breadcrumb'] === 'Escalations') {
      const result = this.userAccess.checkIfUserHasToEscalation();
      this.showAccessRestrictionMessage(result);
      return result;
    }
    if (
      ['User Management', 'Client Management'].some(breadcrumb =>
        route.data['breadcrumb']?.includes(breadcrumb)
      )
    ) {
      const result =
        this.userAccess.checkIfUserHasAccessToClientOrUserManagement();
      this.showAccessRestrictionMessage(result);
      return result;
    }
    const features = route.data['featureNames'] as string[];
    if (!features) return true;

    const hasAccess = this.checkAccess(features, user.allowedFeatures);
    if (!hasAccess) {
      this.loggerService.logError('User does not have access to this feature.');
      return false;
    }
    return true;
  }

  private showAccessRestrictionMessage(result: boolean) {
    if (!result) {
      this.loggerService.logApiError(
        'User does not have access to this feature'
      );
    }
  }

  private checkAccess(
    features: string[],
    allowedFeatures: AllowedFeature[]
  ): boolean {
    const hasAccess = features.every(feature => {
      return allowedFeatures.some(allowedFeature => {
        return allowedFeature.featureName === feature;
      });
    });
    return hasAccess;
  }
}
